/**
 * SafeDatePicker —— 修复「手打日期不生效」（2026-10-23）
 *
 * 🐛 问题（邢先生报）：
 *   antd DatePicker 里手动输入 "2020-03-15"，界面看起来填好了，
 *   但 Form 里拿到的值仍是 null → 保存时提交 null → 后端不写库 → 刷新变空。
 *   典型后果：注册日期填了存不上 → 审验模块一直锁定。
 *
 * ✅ 修法：
 *   在 onBlur 时把输入框文本自己解析成 dayjs，主动写回 Form。
 *   解析失败 → 清空并提示（绝不假装填好了）。
 *
 * 用法与 antd DatePicker 完全一致，直接替换即可：
 *   <SafeDatePicker style={{ width: '100%' }} placeholder="选择日期" />
 *
 * 支持格式：2020-03-15 / 2020/3/15 / 2020.3.15 / 20200315 / 2020年3月15日
 */
import React, { useRef, useCallback } from 'react';
import { DatePicker } from 'antd';
import { message } from 'antd';
import dayjs from 'dayjs';

/** 把用户手输的文本解析成 dayjs；失败返回 null */
export function parseDateText(text) {
  if (!text) return null;
  let s = String(text).trim();
  if (!s) return null;

  // 2020年3月15日 / 2020年03月15日
  s = s.replace(/[年月]/g, '-').replace(/[日号]/g, '');
  // 2020.3.15 / 2020/3/15 → 2020-3-15
  s = s.replace(/[./]/g, '-');
  // 压缩多余分隔符
  s = s.replace(/-+/g, '-').replace(/^-|-$/g, '');

  // 20200315（8位纯数字）
  if (/^\d{8}$/.test(s)) {
    const y = s.slice(0, 4);
    const m = s.slice(4, 6);
    const d = s.slice(6, 8);
    const t = dayjs(`${y}-${m}-${d}`, 'YYYY-MM-DD');
    return t.isValid() ? t : null;
  }

  // YYYY-M-D / YYYY-MM-DD
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (y < 1900 || y > 2200 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const t = dayjs(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`, 'YYYY-MM-DD');
    // 校验真实存在（防 2-31 这类）
    if (!t.isValid() || t.year() !== y || t.month() + 1 !== mo || t.date() !== d) return null;
    return t;
  }

  // 只有年月 YYYY-MM（默认 1 号，月粒度字段用）
  const m2 = s.match(/^(\d{4})-(\d{1,2})$/);
  if (m2) {
    const y = parseInt(m2[1], 10);
    const mo = parseInt(m2[2], 10);
    if (y < 1900 || y > 2200 || mo < 1 || mo > 12) return null;
    return dayjs(`${y}-${String(mo).padStart(2, '0')}-01`, 'YYYY-MM-DD');
  }

  return null;
}

/**
 * 包一层：支持 value/onChange 受控，也支持 Form.Item 注入
 */
export default function SafeDatePicker({
  value,
  onChange,
  format = 'YYYY-MM-DD',
  showTime,
  allowMonthOnly = false,
  ...rest
}) {
  const ref = useRef(null);

  const handleBlur = useCallback(
    (e) => {
      const raw = e && e.target ? e.target.value : '';
      const text = (raw || '').trim();

      // 空 → 清空值
      if (!text) {
        if (value) onChange?.(null, text);
        return;
      }

      const parsed = parseDateText(text);
      if (!parsed) {
        // 解析失败：清空并明确提示（不假装成功）
        message.warning(`日期格式不对：${text}，请重新选择或按「2020-03-15」格式输入`);
        onChange?.(null, '');
        return;
      }

      // 和当前值一致就不动
      // ⭐ 2026-10-24 修（邢先生：「审验时间选完 3 秒后自动没了」）：
      //   月选择器（picker="month"）的值是 2026-03，失焦时文本是 2026-03-01，
      //   原判断 value.isSame(parsed,'day') 在某些情况下为 false → 又调一次 onChange
      //   → 上层 setState → 触发重新拉数据 → 把刚填的值冲掉（表现为“选完就没了”）。
      //   改为：只要「年+月」相同就不重新触发（日粒度字段仍比到天）。
      if (value && dayjs.isDayjs(value) && value.isValid()) {
        const isMonthPicker = rest.picker === 'month';
        if (isMonthPicker) {
          if (value.year() === parsed.year() && value.month() === parsed.month()) return;
        } else if (!showTime && value.isSame(parsed, 'day')) {
          return;
        }
      }
      // ⭐ 2026-10-24 再修（邢先生：「手动输入可以，选择的日期就不行」）：
      //   选择日期后 antd 会先用内部状态把文本回写进 input，紧接着触发 blur。
      //   此时 prop 里的 value 还是旧的（React 异步更新），年/月比较可能不相等 →
      //   又走一遍 onChange，把上层刚设的值反覆盖成空。
      //   底线兜底：只要 input 里显示的文本和当前 value 格式化后的文本一致，就直接返回。
      try {
        const shown = String(text || '');
        const cur = value && dayjs.isDayjs(value) && value.isValid() ? value.format(format) : '';
        if (shown && cur && shown === cur) return;
      } catch (e) { /* ignore */ }
      onChange?.(parsed, parsed.format(format));
    },
    [value, onChange, format, showTime, rest.picker]
  );

  // ⚠️ 关键：不用 inputReadOnly（要允许手打），靠 onBlur 兜底解析。
  //   同时关掉 antd 自带的「输入后失焦自动清空」的迷惑行为由我们自己接管。
  return (
    <DatePicker
      ref={ref}
      value={value}
      onChange={(v) => onChange?.(v, v ? v.format(format) : '')}
      onBlur={handleBlur}
      format={format}
      showTime={showTime}
      {...rest}
    />
  );
}
